import React, { useEffect, useState } from "react";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/styles";
import { Card } from "../../components/Card";
import {
    Button,
    Stack,
    Chip,
    Box,
    Snackbar,
    Alert,
    CircularProgress,
} from "@mui/material";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";

import API_ENDPOINTS from "../../apis/endpoints";
import useAuth from "../../context";
import { Close } from "@mui/icons-material";

function ImageUpload({ attachments, onChange, onUploadingChange }) {
    const [isUploading, setIsUploading] = useState(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const auth = useAuth();

    const [uploadedFiles, setUploadedFiles] = useState(() => {
        if (!attachments) {
            return [];
        }

        return attachments.map((file) => ({
            id: file.id,
            name: file.name,
            uploaded: true,
        }));
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    const storedDeviceId = localStorage.getItem("device-id");

    const handleFileUpload = async (e) => {
        onUploadingChange(true);
        const files = Array.from(e.target.files);
        const newFiles = []; // Temporary array to hold new file IDs

        for (const file of files) {
            try {
                const formData = new FormData();
                formData.append("file_name", file.name);
                formData.append("file_type", file.type);
                formData.append("file_size", file.size.toString());
                formData.append("folder_id", id);
                formData.append("folder_type", "newsletter_attachments");

                // Make the API call to upload the file
                const response = await fetch(`${API_ENDPOINTS.STORAGE}`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                    },
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error(`Failed to upload file: ${file.name}`);
                }

                const uploadData = await response.json();

                if (!uploadData.success) {
                    throw new Error(
                        uploadData.message || "Failed to upload file"
                    );
                }

                const { file_id, file_name, upload_url } = uploadData.data;

                setUploadedFiles((files) => [
                    ...files,
                    {
                        id: file_id,
                        name: file_name,
                        uploaded: false,
                    },
                ]);

                const uploadRes = await fetch(upload_url, {
                    method: "PUT",
                    headers: {
                        "Content-Type": file.type,
                    },
                    body: file,
                });

                if (!uploadRes.ok) {
                    throw new Error(`Failed to upload file: ${file.name}`);
                }

                setUploadedFiles((files) =>
                    files.map((file) => {
                        if (file.id === file_id) {
                            return {
                                ...file,
                                uploaded: true,
                            };
                        }

                        return file;
                    })
                );
            } catch (error) {
                console.error("Error uploading file:", error);
                setSnackbar({
                    open: true,
                    message: error.message || "Failed to upload files",
                    severity: "error",
                });
            }
        }
        onUploadingChange(false);
    };

    const handleFileRemove = (id) => {
        setUploadedFiles((files) => {
            return files.filter((file) => file.id !== id);
        });
        // Optionally remove the corresponding ID if needed
    };

    useEffect(() => {
        onChange?.(uploadedFiles.map((file) => file.id));
    }, [uploadedFiles]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <>
            <Stack direction="column" spacing={2} marginY={2}>
                <Button
                    variant="outlined"
                    component="label"
                    sx={{ alignSelf: "flex-start" }}
                >
                    <AttachFileRoundedIcon sx={{ width: "16px" }} /> &nbsp;
                    Attach Files
                    <input
                        hidden
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                    />
                </Button>

                <Stack spacing={1} sx={{ width: "26rem" }}>
                    {uploadedFiles.map((file) => (
                        <Box
                            key={file.id}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Chip
                                sx={{
                                    width: "20rem",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    paddingX: "10px",
                                    height: "2.5rem",
                                }}
                                label={file.name}
                                variant="outlined"
                                onDelete={() => {
                                    file.uploaded && handleFileRemove(file.id);
                                }}
                                deleteIcon={
                                    file.uploaded ? (
                                        <span
                                            style={{
                                                cursor: "pointer",
                                                alignSelf: "center",
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Close />
                                        </span>
                                    ) : (
                                        <span>
                                            <CircularProgress size={20} />
                                        </span>
                                    )
                                }
                            />
                        </Box>
                    ))}
                </Stack>
            </Stack>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default ImageUpload;
